import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, TrendingUp } from 'lucide-react';
import { AccesRefuse, AlertBanner, PageHeader, Skeleton, StatusBadge } from '@/components/ui';
import { usePerms } from '@/hooks/usePerms';
import { listChantiers } from '@/services/chantiers';
import { CHANTIER_STATUT } from '@/modules/chantiers/statuts';

/**
 * Vue d'entrée du contrôle des coûts (périmètre financier RÉSERVÉ) : sélection
 * d'un chantier pour accéder à son budget, contrôle des coûts, bilan et situations.
 * L'accès est verrouillé par la permission « controle_couts » (côté serveur aussi).
 */
export function ControleCoutsPage() {
  const perms = usePerms('controle_couts');
  const { data, isLoading } = useQuery({
    queryKey: ['chantiers'],
    queryFn: () => listChantiers(),
    enabled: perms.canConsulter,
  });

  if (!perms.canConsulter) {
    return <AccesRefuse detail="Le contrôle des coûts (budget, marges, bilan financier) requiert la permission « Contrôle des coûts ». Contactez votre administrateur." />;
  }

  return (
    <>
      <PageHeader
        title="Contrôle des coûts"
        subtitle="Analyse prévu / engagé / réalisé par chantier"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Contrôle des coûts' }]}
      />

      <AlertBanner variant="info" className="mb-6">
        Sélectionnez un chantier pour consulter le contrôle des coûts détaillé (vues « coût réel total » et
        « coût comptabilisé »).
      </AlertBanner>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          {data?.items.map((c) => {
            const st = CHANTIER_STATUT[c.statut];
            return (
              <Link
                key={c.id}
                to={`/couts/${c.id}`}
                className="flex items-center gap-4 border-b border-border px-5 py-4 transition-colors last:border-0 hover:bg-surface-hover"
              >
                <span className="flex size-9 items-center justify-center rounded-md bg-brand-primary/10 text-brand-primary">
                  <TrendingUp className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-content-primary">{c.nom}</p>
                  <p className="text-xs text-content-muted">{c.code}</p>
                </div>
                <StatusBadge status={st.status} label={st.label} />
                <ChevronRight className="size-4 text-content-muted" />
              </Link>
            );
          })}
          {data && data.items.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-content-muted">Aucun chantier à analyser.</p>
          )}
        </div>
      )}
    </>
  );
}
